/**
 * Helper (pure) ของกฎ "ฉากนี้กด generate วิดีโอได้หรือยัง"
 *
 * แยกออกมาจาก `CardGenerateDirectScript.getVideoModeIssues` เพราะกฎนี้เป็นตัวบล็อกก่อนยิง
 * request ที่ **เสียเงินจริง** แต่เดิมฝังอยู่ใน component 5,000 บรรทัดที่เทสต์ไม่ได้
 * (jsdom ของ repo นี้ render component ไม่ได้ — native module `canvas` ผูกกับ Node คนละเวอร์ชัน)
 *
 * ตัว component ยังรับผิดชอบเรื่องรวบรวมข้อมูลจาก state (เปิดโหมดวิดีโอไหม / มีวิดีโอแล้วไหม)
 * แล้วส่งเข้ามาทาง `ctx` — ไฟล์นี้ตัดสินอย่างเดียว
 */
import { ScriptWithImageItem, VideoModelCatalog } from "../types/MeGenTypes";
import { effectiveVideoModel, videoModelRequiresAnimPrompt } from "./videoModelCatalog";

export type SceneVideoContext = {
  /** เปิดโหมดวิดีโอให้ฉากนี้ไหม — เป็น state ของหน้าจอ (`localUseAnimated`) ไม่ได้อยู่ใน item */
  useAnimated: boolean;
  /** มีวิดีโออยู่แล้วไหม (อัปโหลดเอง หรือ generate ไว้รอบก่อน) */
  hasVideo: boolean;
  /** catalog โมเดล video — `null` = โหลดไม่สำเร็จ/ยังไม่มา ⇒ ตอบไม่ได้ว่าโมเดลต้องมี prompt ไหม */
  catalog: VideoModelCatalog | null;
};

/**
 * `true` = ฉากนี้ยังสร้างวิดีโอไม่ได้ ต้องบล็อกก่อนยิง request
 *
 * กฎ: เปิดโหมดวิดีโอแล้วต้องมีอย่างน้อยอย่างหนึ่ง — วิดีโอที่มีอยู่แล้ว, Video Prompt,
 * หรือเลือกโมเดลที่ไม่ต้องใช้ Video Prompt (lip-sync)
 */
export const sceneBlocksVideoGenerate = (
  item: ScriptWithImageItem,
  ctx: SceneVideoContext,
): boolean => {
  if (item.active === false) return false; // ฉากถูกปิด = ไม่ถูกสร้างอยู่แล้ว
  if (!ctx.useAnimated) return false; // ไม่ได้เปิดโหมดวิดีโอ = ไม่มีการสร้างวิดีโอ
  if (ctx.hasVideo) return false; // มีวิดีโออยู่แล้ว = ไม่ต้องสร้างใหม่
  if (item.anim_prompt?.trim()) return false; // มี Video Prompt = สร้างได้

  // เทียบจากโมเดลที่หลังบ้าน **จะใช้จริง** ไม่ใช่ค่าใน item — ตอนเลือก "Use Global Default"
  // ค่าใน item เป็น "" แต่ตัวที่รันคือ catalog.default ซึ่งอาจเป็น lip-sync
  //
  // `!== false` ไม่ใช่ `=== true`: `null` แปลว่า "ตอบไม่ได้" (ไม่มี catalog / ไม่รู้จัก id)
  // ⇒ ตกกลับไปใช้กฎเดิมคือบล็อก · ผ่อนเฉพาะตอนที่รู้แน่ชัดว่าโมเดลไม่ต้องใช้ prompt
  const needsPrompt =
    videoModelRequiresAnimPrompt(effectiveVideoModel(item, ctx.catalog), ctx.catalog) !== false;

  return needsPrompt;
};
